import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { classroomApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, BookOpen, ArrowLeft, ClipboardCopy, GraduationCap } from 'lucide-react';

const CLASS_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#84cc16'];

export default function ClassroomHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [form, setForm] = useState({ name: '', section: '', subject: '', description: '', color: CLASS_COLORS[0] });
  const [joinCode, setJoinCode] = useState('');
  const isTeacher = ['super_admin', 'org_admin', 'faculty'].includes(user?.role);

  useEffect(() => { loadClasses(); }, []);

  const loadClasses = async () => {
    try {
      const data = await classroomApi.getClasses();
      setClasses(data.classes || []);
    } catch { toast.error('Failed to load classes'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Class name is required'); return; }
    try {
      const cls = await classroomApi.createClass(form);
      setClasses(prev => [cls, ...prev]);
      setShowCreate(false);
      setForm({ name: '', section: '', subject: '', description: '', color: CLASS_COLORS[0] });
      toast.success(`Class "${cls.name}" created! Code: ${cls.class_code}`);
    } catch (e) { toast.error(e.message); }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) { toast.error('Enter a class code'); return; }
    try {
      const res = await classroomApi.joinClass(joinCode);
      toast.success(`Joined "${res.class_name}"!`);
      setShowJoin(false);
      setJoinCode('');
      loadClasses();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="classroom-hub">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <GraduationCap className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold text-foreground">Classroom</h1>
          </div>
          <div className="flex gap-2">
            {isTeacher && (
              <Button onClick={() => setShowCreate(true)} data-testid="create-class-btn">
                <Plus className="w-4 h-4 mr-1" /> Create Class
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowJoin(true)} data-testid="join-class-btn">
              Join Class
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />)}
          </div>
        ) : classes.length === 0 ? (
          <div className="text-center py-20">
            <GraduationCap className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No classes yet</h2>
            <p className="text-muted-foreground mb-6">{isTeacher ? 'Create your first class to get started.' : 'Join a class using a class code from your teacher.'}</p>
            <Button onClick={() => isTeacher ? setShowCreate(true) : setShowJoin(true)}>
              {isTeacher ? 'Create Class' : 'Join Class'}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classes.map(cls => (
              <Link key={cls.class_id} to={`/classroom/${cls.class_id}`} className="group block" data-testid={`class-card-${cls.class_id}`}>
                <div className="rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow bg-card">
                  <div className="h-24 p-4 flex flex-col justify-end" style={{ backgroundColor: cls.color || '#6366f1' }}>
                    <h3 className="text-lg font-bold text-white truncate">{cls.name}</h3>
                    {cls.section && <p className="text-white/80 text-sm">{cls.section}</p>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {cls.subject && <Badge variant="secondary">{cls.subject}</Badge>}
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {cls.student_count || 0}</span>
                      </div>
                      {isTeacher && (
                        <span className="text-xs text-muted-foreground font-mono">{cls.class_code}</span>
                      )}
                    </div>
                    {isTeacher && <p className="text-xs text-muted-foreground mt-2">Teacher: {cls.teacher_name}</p>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Create Class Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-class-dialog">
          <DialogHeader><DialogTitle>Create a Class</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">Class Name *</label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Mathematics" data-testid="class-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground">Section</label>
                <Input value={form.section} onChange={e => setForm(p => ({...p, section: e.target.value}))} placeholder="e.g. 10-A" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Subject</label>
                <Input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} placeholder="e.g. Algebra" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Description</label>
              <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Optional description" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Color</label>
              <div className="flex gap-2">
                {CLASS_COLORS.map(c => (
                  <button key={c} onClick={() => setForm(p => ({...p, color: c}))}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-class-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Class Dialog */}
      <Dialog open={showJoin} onOpenChange={setShowJoin}>
        <DialogContent data-testid="join-class-dialog">
          <DialogHeader><DialogTitle>Join a Class</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Ask your teacher for the class code, then enter it here.</p>
          <Input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. ABC123" maxLength={6} className="text-center text-lg font-mono tracking-widest" data-testid="join-code-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoin(false)}>Cancel</Button>
            <Button onClick={handleJoin} data-testid="join-class-submit">Join</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
